// Sample data ("explore mode") state for a nursery.
//
// A brand new signup can choose to fill their OWN account with the single
// shared sample nursery dataset (the same one behind the sales demo). While
// sample data is active, real children and staff must not be added and the
// account must not be upgraded, so everything stays cleanly separated.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SampleDataState {
  isSampleData: boolean;
  seededAt: string | null;
  isLoading: boolean;
}

export function useSampleData(): SampleDataState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["sample-data-state", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("sample_data_active, sample_data_seeded_at")
        .maybeSingle();
      if (error) return null;
      return data;
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    isSampleData: !!data?.sample_data_active,
    seededAt: (data?.sample_data_seeded_at as string | null) ?? null,
    isLoading,
  };
}

export function useStartSampleExploration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("start_sample_exploration");
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useClearSampleData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("clear_sample_data");
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
